import { Link, useLocation } from 'react-router-dom';
import { Code, Zap, LogOut, User } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const Navbar = () => {
  const location = useLocation();
  const {
    isAuthenticated,
    loginWithRedirect,
    logout,
    user,
    getAccessTokenSilently,
    isLoading
  } = useAuth0();

  const isActive = (path: string) =>
    location.pathname === path;

  // ── Auth-gated nav click ──────────────────────
  const handleAuthNav = (
    e: React.MouseEvent,
    path: string
  ) => {
    if (!isAuthenticated) {
      e.preventDefault();
      loginWithRedirect({
        appState: { returnTo: path }
      });
    }
  };

  const [isPro, setIsPro] = useState(false);

    useEffect(() => {
      if (!isAuthenticated) return;

      const checkPro = async () => {
        try {
          const token = await
            getAccessTokenSilently({
              authorizationParams: {
                audience:
                  "https://api.scaffoldbridge.io"
              }
            });

          const response = await axios.get(
            `${process.env.REACT_APP_API_URL
              || 'http://localhost:5062'
            }/api/billing/status`,
            { headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          setIsPro(response.data.isPro);
        } catch {}
      };

      checkPro();
    }, [isAuthenticated]);

  return (
    <nav className="bg-gray-900 border-b
                    border-gray-800 sticky
                    top-0 z-50">
      <div className="max-w-7xl mx-auto
                      px-4 sm:px-6 lg:px-8">
        <div className="flex items-center
                        justify-between h-16">

          {/* Logo */}
          <Link to="/"
                className="flex items-center
                           space-x-2">
            <div className="bg-blue-600 p-1.5
                            rounded-lg">
              <Code className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl
                             text-white">
              Scaffold
              <span className="text-blue-500">
                Bridge
              </span>
            </span>
          </Link>

          {/* Nav Links */}
          <div className="flex items-center space-x-1">

            {/* Home — always visible ✅ */}
            <Link to="/"
                  className={`px-3 py-2 rounded-lg
                             text-sm font-medium
                             transition-colors ${
                isActive('/')
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}>
              Home
            </Link>

            {/* Builder — auth gated ✅ */}
            <Link
              to="/builder"
              onClick={e => handleAuthNav(e, '/builder')}
              className={`px-3 py-2 rounded-lg
                         text-sm font-medium
                         transition-colors ${
                isActive('/builder')
                  ? 'bg-gray-800 text-white'
                  : isAuthenticated
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-600 hover:text-gray-400 cursor-pointer'
              }`}
              title={!isAuthenticated
                ? 'Login to access Builder'
                : undefined}>
              Builder
              {/* Lock icon when not logged in */}
              {!isAuthenticated && (
                <span className="ml-1 text-gray-600
                                 text-xs">🔒</span>
              )}
            </Link>

            {/* Generator — auth gated ✅ */}
            <Link
              to="/generator"
              onClick={e => handleAuthNav(e, '/generator')}
              className={`px-3 py-2 rounded-lg
                         text-sm font-medium
                         transition-colors ${
                isActive('/generator')
                  ? 'bg-gray-800 text-white'
                  : isAuthenticated
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-600 hover:text-gray-400 cursor-pointer'
              }`}
              title={!isAuthenticated
                ? 'Login to access Generator'
                : undefined}>
              Generator
              {/* Lock icon when not logged in */}
              {!isAuthenticated && (
                <span className="ml-1 text-gray-600
                                 text-xs">🔒</span>
              )}
            </Link>

            {/* MCP — Coming Soon ✅ */}
            <div className="flex items-center gap-1.5
                            px-3 py-2 rounded-lg text-sm
                            font-medium transition-colors
                            text-gray-400 cursor-not-allowed
                            opacity-60">
              <span>MCP</span>
              <span className="bg-purple-500/20
                               text-purple-400 text-xs
                               px-1.5 py-0.5 rounded-full
                               font-mono border
                               border-purple-500/30">
                Soon
              </span>
            </div>
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <div className="w-8 h-8 rounded-full
                              bg-gray-700 animate-pulse"/>

            ) : isAuthenticated ? (
              <div className="flex items-center gap-2">

                {/* User info */}
                <div className="flex items-center gap-2
                                bg-gray-800 border border-gray-700
                                px-3 py-1.5 rounded-lg">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || 'User'}
                      className="w-6 h-6 rounded-full"
                    />
                  ) : (
                    <User className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-gray-300 text-sm
                                   max-w-[100px] truncate">
                    {user?.name?.split(' ')[0] || user?.email}
                  </span>
                  
                              {/* Pro badge ✅ */}
                              {isPro && (
                                <span className="bg-blue-500/20
                                                text-blue-400 text-xs
                                                px-1.5 py-0.5
                                                rounded-full font-mono
                                                border border-blue-500/30">
                                  Pro ⚡
                                </span>
                              )}
                              
                        {isAuthenticated && (
                          <Link to="/billing"
                                className="text-gray-400
                                          hover:text-white text-sm
                                          px-3 py-2 rounded-lg
                                          transition-colors">
                            ⚙️ Billing
                          </Link>
                        )}
                </div>

                {/* Logout */}
                <button
                  onClick={() => logout({
                    logoutParams: {
                      returnTo: window.location.origin
                    }
                  })}
                  title="Logout"
                  className="flex items-center gap-1
                             text-gray-400 hover:text-red-400
                             hover:bg-red-500/10 px-3 py-2
                             rounded-lg transition-colors text-sm">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>

            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loginWithRedirect()}
                  className="text-gray-400 hover:text-white
                             text-sm px-4 py-2 rounded-lg
                             transition-colors">
                  Login
                </button>

                <button
                  onClick={() => loginWithRedirect()}
                  className="flex items-center gap-1.5
                             bg-blue-600 hover:bg-blue-500
                             text-white text-sm font-semibold
                             px-4 py-2 rounded-lg
                             transition-colors">
                  <Zap className="w-4 h-4" />
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;